/*
 * Copyright © 2017-2026 Knowledge for Decisions (K4D). All rights reserved.
 *
 * This code and associated materials are protected by copyright laws and international
 * treaties. Unauthorized use, reproduction, distribution, or modification of any content
 * from Knowledge for Decisions (K4D) is strictly prohibited. For inquiries regarding
 * the use or licensing of K4D's copyrighted materials, please contact us at
 * contact@k4d.com.br. "Knowledge for Decisions (K4D)" and the slogan "Using data to make
 * precise decisions" are registered trademarks of Knowledge for Decisions (K4D).
 */

import React from 'react';
import {
  Avatar as ShadcnAvatar,
  AvatarImage,
  AvatarFallback,
} from '@/shared/ui/components/shadcn/avatar';
import { cn } from '@/shared/ui/utils/tailwind';
import { readableInkOn } from '@/shared/utils/color-contrast';

export type AvatarProps = {
  src?: string;
  alt?: string;
  size?: number | string;
  radius?: number | string;
  color?: string;
  referrerPolicy?: React.HTMLAttributeReferrerPolicy;
  children?: React.ReactNode;
  className?: string;
  style?: React.CSSProperties;
};

/**
 * Avatar adapter that wraps shadcn Avatar primitives.
 *
 * @param {AvatarProps} props - Avatar properties.
 * @returns {JSX.Element} Rendered avatar.
 */
export const Avatar: React.FC<AvatarProps> = ({
  src,
  alt,
  size = 40,
  radius = '50%',
  color,
  referrerPolicy,
  children,
  className,
  style,
}) => {
  const sizeValue = typeof size === 'number' ? `${size}px` : size;
  const fallbackRef = React.useRef<HTMLSpanElement>(null);
  const [ink, setInk] = React.useState<string | null>(null);

  /* The fallback initial used to inherit the surrounding ink, so an avatar
     painted with a dark `color` (the app header passes `var(--seed-base)`)
     rendered a #1a1a1a letter on #4a0e11 — 1.13:1, invisible. The prop
     arrives as a CSS value the browser has to resolve, so the ink is derived
     from the computed background of the node that actually paints it. */
  React.useLayoutEffect(() => {
    const node = fallbackRef.current;
    if (!color || !node) {
      setInk(null);
      return;
    }
    setInk(readableInkOn(window.getComputedStyle(node).backgroundColor));
  }, [color]);

  return (
    <ShadcnAvatar
      className={cn(className)}
      style={{
        width: sizeValue,
        height: sizeValue,
        backgroundColor: color,
        ...style,
      }}
    >
      {src && <AvatarImage src={src} alt={alt || ''} referrerPolicy={referrerPolicy} />}
      {/* The background is repeated here, not just on the wrapper: the shadcn
          fallback ships its own `bg-muted` covering the wrapper, so ink and
          background have to live on the same node or the pair depends on
          which rule wins the cascade. */}
      <AvatarFallback
        ref={fallbackRef}
        style={{ backgroundColor: color, color: ink ?? undefined }}
      >
        {children || alt?.[0]?.toUpperCase() || '?'}
      </AvatarFallback>
    </ShadcnAvatar>
  );
};

export default Avatar;
